import { useEffect } from "react";

export type MidStreamListener = (threadId: string) => void;

export type MidStreamChannel = {
    emit: (threadId: string) => void;
    subscribe: (listener: MidStreamListener) => () => void;
};

export function createMidStreamChannel(): MidStreamChannel {
    const listeners = new Set<MidStreamListener>();
    return {
        emit: (threadId) => {
            listeners.forEach((listener) => {
                try {
                    listener(threadId);
                } catch (err) {
                    console.error("mid-stream error listener threw", err);
                }
            });
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

/**
 * App-wide channel connecting the model adapter to the mounted runtime
 * reloader. Tests build their own channel instead of sharing this one.
 */
export const midStreamRecoveryChannel = createMidStreamChannel();

export type UseMidStreamRecoveryReloadArgs = {
    /** Read at emit time, so a thread switch cannot leave a stale id captured. */
    getOwnThreadId: () => string | undefined;
    reload: (threadId: string) => Promise<unknown>;
    channel?: MidStreamChannel;
};

/**
 * Re-read one thread's history after a post-admission stream interruption.
 *
 * Resolve the subscriber's thread at emit time so a thread switch or another
 * mounted consumer cannot import a different conversation's messages.
 */
export function useMidStreamRecoveryReload({
    getOwnThreadId,
    reload,
    channel = midStreamRecoveryChannel,
}: UseMidStreamRecoveryReloadArgs): void {
    useEffect(() => {
        const listener: MidStreamListener = (threadId) => {
            // Only this thread's own failure. An unknown id means the thread has
            // no server row yet, so there is nothing to re-read either way.
            if (getOwnThreadId() !== threadId) return;
            // A failed durable read leaves the local error state as the fallback.
            // Report it so a wrong-target import and a failed reload stay distinct.
            void reload(threadId).catch((err: unknown) => {
                console.error("mid-stream recovery reload failed", err);
            });
        };
        return channel.subscribe(listener);
    }, [channel, getOwnThreadId, reload]);
}
